import * as React from "react";

import { Alert, Button, FormField, Input, notify, Switch, Textarea } from "Components/ui";
import { InvoiceUpdateInput, LineItemInput } from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import { isDateOnly, todayIST } from "utils/format";
import DialogShell from "Routes/Billing/Components/DialogShell";
import { DateInput } from "Routes/Billing/Components/ui";
import { closeAfterFlush } from "Routes/Billing/hooks";
import { useInvoiceActions } from "../data";
import { InvoiceEditorState, LineItemDraft } from "../types";
import { draftsFromItems, newLineItem, validateLineItems } from "../lineItems";
import LineItemsEditor from "./LineItemsEditor";

interface InvoiceEditorModalProps {
  tenantId: string;
  state: InvoiceEditorState;
  warningDays: number;
  onClose: () => void;
}

const comparableItems = (items: LineItemInput[]) =>
  JSON.stringify(items.map((item) => [item.description, item.qty, item.unitPrice]));

/** One-time invoice (opening balance, advance, set-up fee…) or edit of a draft. */
const InvoiceEditorModal: React.FC<InvoiceEditorModalProps> = ({
  tenantId,
  state,
  warningDays,
  onClose,
}) => {
  const invoice = state.mode === "edit" ? state.invoice : null;
  const today = todayIST();
  const [title, setTitle] = React.useState<string>(invoice?.title || "");
  const [items, setItems] = React.useState<LineItemDraft[]>(
    invoice ? draftsFromItems(invoice.items) : [newLineItem()]
  );
  const [dueDate, setDueDate] = React.useState<string>(invoice ? invoice.dueDate : "");
  const [notes, setNotes] = React.useState<string>(invoice?.notes || "");
  const [issueNow, setIssueNow] = React.useState<boolean>(false);
  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const actions = useInvoiceActions(tenantId);

  const lineValidation = validateLineItems(items);
  const errors = {
    title: title.trim().length > 120 ? "Keep the title under 120 characters." : null,
    dueDate: isDateOnly(dueDate) ? null : "Pick a due date.",
    notes: notes.length > 1000 ? "Keep notes under 1000 characters." : null,
  };
  const hasErrors = Object.values(errors).some(Boolean) || !lineValidation.items;
  const pastDue = isDateOnly(dueDate) && dueDate < today;

  const handleSave = async () => {
    setShowErrors(true);
    setSubmitError(null);
    if (hasErrors || !lineValidation.items) {
      return;
    }
    setSaving(true);
    try {
      if (invoice) {
        const update: InvoiceUpdateInput = {};
        if ((title.trim() || null) !== (invoice.title || null)) {
          update.title = title.trim() || null;
        }
        if (comparableItems(lineValidation.items) !== comparableItems(invoice.items)) {
          update.items = lineValidation.items;
        }
        if (dueDate !== invoice.dueDate) {
          update.dueDate = dueDate;
        }
        if ((notes.trim() || null) !== (invoice.notes || null)) {
          update.notes = notes.trim() || null;
        }
        if (Object.keys(update).length === 0) {
          notify.info("Nothing changed");
          onClose();
          return;
        }
        await actions.update.mutateAsync({ invoiceId: invoice._id, input: update });
      } else {
        await actions.create.mutateAsync({
          title: title.trim() || undefined,
          items: lineValidation.items,
          dueDate,
          notes: notes.trim() || undefined,
          issueNow,
        });
      }
      closeAfterFlush(onClose);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <DialogShell
      title={invoice ? "Edit draft invoice" : "One-time invoice"}
      subtitle={
        invoice
          ? "Drafts can be changed freely until they are issued."
          : "For opening balances, advance payments, set-up fees or anything outside the recurring charges."
      }
      size="lg"
      onClose={saving ? () => undefined : onClose}
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            {invoice ? "Save draft" : issueNow ? "Create and issue" : "Save as draft"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <FormField
          label="Title"
          id="invoice-title"
          error={errors.title || undefined}
          hint="Optional, e.g. “Opening balance” or “Advance for FY 26-27”."
        >
          <Input
            type="text"
            value={title}
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
          />
        </FormField>

        <div>
          <p className="mb-2 text-sm font-medium text-foreground">Line items</p>
          <LineItemsEditor
            items={items}
            onChange={setItems}
            errors={lineValidation.errors}
            total={lineValidation.total}
            totalError={lineValidation.totalError}
            showErrors={showErrors}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Due date"
            id="invoice-due"
            required
            error={(showErrors && errors.dueDate) || undefined}
            hint={
              pastDue
                ? `Already past: the client is overdue once this is issued (TeleOpd still warns for ${warningDays} days first).`
                : `TeleOpd warnings start ${warningDays} days before this date.`
            }
          >
            <DateInput
              id="invoice-due"
              value={dueDate}
              onChange={setDueDate}
              hasError={showErrors && Boolean(errors.dueDate)}
            />
          </FormField>
          {!invoice && (
            <Switch
              checked={issueNow}
              onCheckedChange={setIssueNow}
              label="Issue now"
              description={
                issueNow
                  ? "Gets an invoice number and a pay link right away; reminders follow the client's schedule."
                  : "Saved as a draft. Review it, then issue it from the Invoices tab."
              }
            />
          )}
        </div>

        <FormField
          label="Notes"
          id="invoice-notes"
          error={errors.notes || undefined}
          hint="Kept with the invoice for your team; not shown on the pay page."
        >
          <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </FormField>

        {submitError && (
          <Alert tone="danger" title="Not saved">
            {submitError}
          </Alert>
        )}
      </div>
    </DialogShell>
  );
};

export default InvoiceEditorModal;
